import React from 'react';

const Rules: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">📋 Game Rules</h2>
        <p className="text-gray-300">Token Launch League - Competitive FDV Prediction Game</p>
      </div>

      <div className="grid gap-6">
        {/* Game Overview */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎯 Game Overview</h3>
          <div className="space-y-4 text-gray-300">
            <p>
              Token Launch League is a competitive prediction game where participants forecast 
              the Final Diluted Valuation (FDV) 24 hours post-TGE. Players allocate chips to 
              predictions, with accuracy determining point allocation and chip multipliers.
            </p>
            <p>
              The participant with the highest cumulative points across all TGEs wins. 
              Ties are resolved by remaining chip count.
            </p>
          </div>
        </div>

        {/* How to Play */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎮 How to Play</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-white mb-3">📝 Making Predictions</h4>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-primary">1.</span>
                  <span>Browse available TGEs on the dashboard</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-primary">2.</span>
                  <span>Click "PLACE PREDICTION" on any open TGE</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-primary">3.</span>
                  <span>Enter your predicted FDV value (in millions USD)</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-primary">4.</span>
                  <span>Allocate chips to your prediction (10-50 chips)</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-primary">5.</span>
                  <span>Submit before the prediction deadline</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">💰 What You're Betting On</h4>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-accent">🎯</span>
                  <span>Predict the FDV value 24 hours after the TGE</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-accent">⏰</span>
                  <span>Predictions close 1 day before TGE</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-accent">📊</span>
                  <span>Accuracy determines points and chip multipliers</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 mt-1 text-accent">🏆</span>
                  <span>Highest total points wins the competition</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Betting Rules */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎰 Betting Rules</h3>
          <div className="space-y-4 text-gray-300">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-white mb-3">💰 Chip System</h4>
                <div className="space-y-3 text-gray-300">
                  <div className="flex justify-between items-center p-3 bg-background-middle/30 rounded-lg">
                    <span>Starting Allocation:</span>
                    <span className="text-primary font-bold">100 chips</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-background-middle/30 rounded-lg">
                    <span>Minimum Bet:</span>
                    <span className="text-primary font-bold">10 chips</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-background-middle/30 rounded-lg">
                    <span>Maximum Bet:</span>
                    <span className="text-primary font-bold">50 chips</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-background-middle/30 rounded-lg">
                    <span>Bet Range:</span>
                    <span className="text-primary font-bold">10-50 chips</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Game Mechanics */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">⚙️ Game Mechanics</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-white mb-3">🎯 Prediction Accuracy</h4>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center justify-between p-2 bg-green-900/20 rounded">
                  <span>±5% accuracy:</span>
                  <span className="text-green-400 font-bold">2x chips</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-yellow-900/20 rounded">
                  <span>±10% accuracy:</span>
                  <span className="text-yellow-400 font-bold">1.5x chips</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-900/20 rounded">
                  <span>±20% accuracy:</span>
                  <span className="text-blue-400 font-bold">1x chips</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-red-900/20 rounded">
                  <span>Beyond ±20%:</span>
                  <span className="text-red-400 font-bold">Lose chips</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">🏆 Competition Rules</h4>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <span className="mr-2 mt-1">👥</span>
                  <span>Individual competition - no teams</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">📈</span>
                  <span>Points accumulate across all TGEs</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">🏅</span>
                  <span>Highest total points wins</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">⚖️</span>
                  <span>Ties broken by chips remaining</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Rules; 